import { useCallback, useMemo, useRef, useState } from 'react';
import { compact } from 'lodash';

import { BadgeOptionRenderData } from './types';

export type SelectedBadgeOption = BadgeOptionRenderData & { value: string };

type UseBadgeSelectSingleParams = {
  multiple: false;
  value?: string;
  onChange?: (value: string) => void;
};

type UseBadgeSelectMultipleParams = {
  multiple: true;
  value?: string[];
  onChange?: (value: string[]) => void;
};

export type UseBadgeSelectParams = (
  | UseBadgeSelectSingleParams
  | UseBadgeSelectMultipleParams
) & {
  disabled?: boolean;
};

export const useBadgeSelect = (params: UseBadgeSelectParams) => {
  const optionsRegistry = useRef(new Map<string, BadgeOptionRenderData>());
  const [, setVersion] = useState(0);

  const selectedValues = useMemo(
    () => compact(params.multiple ? params.value : [params.value]),
    [params.multiple, params.value],
  );

  const selectedOptions = selectedValues.flatMap<SelectedBadgeOption>(v => {
    const data = optionsRegistry.current.get(v);
    return data ? [{ ...data, value: v }] : [];
  });

  const registerOption = useCallback(
    (optionValue: string, data: BadgeOptionRenderData) => {
      optionsRegistry.current.set(optionValue, data);
      if (selectedValues.includes(optionValue)) setVersion(v => v + 1);
    },
    [selectedValues],
  );

  const unregisterOption = useCallback(
    (optionValue: string) => {
      optionsRegistry.current.delete(optionValue);
      if (selectedValues.includes(optionValue)) setVersion(v => v + 1);
    },
    [selectedValues],
  );

  const handleMultiSelect = (optionValue: string) => {
    if (!params.multiple) return;
    const current = params.value ?? [];
    const next = current.includes(optionValue)
      ? current.filter(v => v !== optionValue)
      : [...current, optionValue];
    params.onChange?.(next);
  };

  const handleSingleSelect = (optionValue: string) => {
    if (params.multiple) return;
    params.onChange?.(optionValue);
    const htmlActiveElement = document.activeElement as HTMLElement;
    htmlActiveElement?.blur();
  };

  const handleSelect = (optionValue: string) => {
    if (params.disabled) return;
    if (params.multiple) handleMultiSelect(optionValue);
    else handleSingleSelect(optionValue);
  };

  return {
    selectedValues,
    selectedOptions,
    handleSelect,
    registerOption,
    unregisterOption,
  };
};
